import { useEffect, useState } from 'react';

import { css, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';
import { Button } from 'src/libraries/design/components/button/src/button';

import { token } from '@atlaskit/tokens';

import {
	getConversionTime,
	MAX_TIMEOUT_MS,
	type WebinarMode,
} from '../../webinar-form/src/webinar-form';
import { webinarFormMessages } from '../../webinar-form/src/webinar-form-messages';

import type { UniformDateTimeValue } from 'src/uniform/utils/uniform-date-time';

const webinarButtonCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
});

const alignmentCss = cssMap({
	center: {
		alignItems: 'center',
	},
	start: {
		alignItems: 'flex-start',
	},
});

type WebinarButtonAlignment = 'center' | 'start';

const getWebinarMode = (conversionTime: number | undefined): WebinarMode => {
	if (conversionTime === undefined) {
		return 'scheduled';
	}

	return conversionTime <= Date.now() ? 'on-demand' : 'scheduled';
};

type WebinarButtonSession = {
	fields?: {
		date?: string | UniformDateTimeValue;
		dateLabel?: string;
	};
};

const getWebinarDisplayDate = (
	sessions: Array<WebinarButtonSession | null>,
	onDemandConversionDateTime: UniformDateTimeValue,
) => {
	let earliestDate: string | undefined;
	let earliestTime = Infinity;

	for (const session of sessions) {
		const date = session?.fields?.date;
		const time = getConversionTime(date ?? '');
		if (time === undefined) {
			return onDemandConversionDateTime;
		}
		if (time < earliestTime) {
			earliestTime = time;
			earliestDate = typeof date === 'string' ? date : date?.datetime;
		}
	}

	return earliestDate ?? onDemandConversionDateTime;
};

export interface UniformWebinarButtonProps {
	onDemandConversionDateTime: UniformDateTimeValue;
	fullUrlPath: string;
	sessions?: Array<WebinarButtonSession | null>;
	alignment?: WebinarButtonAlignment;
}

const UniformWebinarButton = ({
	onDemandConversionDateTime,
	fullUrlPath,
	sessions = [],
	alignment = 'center',
}: UniformWebinarButtonProps) => {
	const intl = useIntl();
	const conversionTime = getConversionTime(onDemandConversionDateTime);
	const displayDate = getWebinarDisplayDate(sessions, onDemandConversionDateTime);
	const displayDateTime = typeof displayDate === 'string' ? displayDate : displayDate?.datetime;
	// Format the stored calendar date without shifting it to the server or viewer timezone.
	const displayTime = displayDateTime
		? getConversionTime(`${displayDateTime.split('T')[0]}T00:00:00.000Z`)
		: undefined;
	const [mode, setMode] = useState<WebinarMode>(() => getWebinarMode(conversionTime));

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const updateMode = () => {
			const nextMode = getWebinarMode(conversionTime);
			setMode(nextMode);

			if (nextMode !== 'scheduled' || conversionTime === undefined) {
				return;
			}

			const remainingTime = conversionTime - Date.now();
			timeoutId = setTimeout(updateMode, Math.min(remainingTime, MAX_TIMEOUT_MS));
		};

		updateMode();

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [conversionTime]);

	const buttonLabel =
		mode === 'on-demand'
			? intl.formatMessage(webinarFormMessages.watchButton)
			: intl.formatMessage(webinarFormMessages.registerButton);

	return (
		<div css={[webinarButtonCss, alignmentCss[alignment]]}>
			{mode === 'scheduled' && displayTime !== undefined && (
				<time dateTime={displayDateTime} className="body-md">
					{intl.formatDate(displayTime, {
						timeZone: 'UTC',
						day: 'numeric',
						month: 'short',
						weekday: 'short',
						year: 'numeric',
					})}
				</time>
			)}
			<Button
				href={fullUrlPath}
				variant={mode === 'on-demand' ? 'primary' : 'primary_alt'}
				dataEventProps={{
					'data-product-key': 'uniform',
					'data-event-component': 'linkButton',
					'data-event-container': 'webinar',
					'data-event': 'clicked',
				}}
			>
				{buttonLabel}
			</Button>
		</div>
	);
};

export default UniformWebinarButton;
